export type MerchantTelemetryValue = string | number | boolean | undefined;
export type MerchantTelemetryPayload = Record<string, MerchantTelemetryValue>;

const SENSITIVE_KEY_PATTERN =
  /(token|secret|password|email|phone|address|url|note|message|customer|contact|actorId|userId|productId|requestId|mappingId|auditId|jobId)/i;

export function dispatchMerchantTelemetry(
  eventName: string,
  payload: MerchantTelemetryPayload,
): void {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("price-rescue:telemetry", {
      detail: {
        eventName: sanitizeTelemetryName(eventName),
        payload: sanitizeTelemetryPayload(payload),
      },
    }),
  );
}

export function sanitizeTelemetryPayload(
  payload: MerchantTelemetryPayload,
): MerchantTelemetryPayload {
  return Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [key, sanitizeTelemetryValue(key, value)]),
  );
}

function sanitizeTelemetryName(eventName: string): string {
  return eventName.replace(/[^a-zA-Z0-9_:-]/g, "_").slice(0, 96);
}

function sanitizeTelemetryValue(
  key: string,
  value: MerchantTelemetryValue,
): MerchantTelemetryValue {
  if (value === undefined) return undefined;
  if (SENSITIVE_KEY_PATTERN.test(key)) return redactedValue(value);
  if (typeof value === "string") return sanitizeStringValue(value);
  return value;
}

function redactedValue(value: MerchantTelemetryValue): MerchantTelemetryValue {
  if (value === undefined) return undefined;
  if (typeof value === "boolean" || typeof value === "number") return value;
  return value.length === 0 ? "" : "[redacted]";
}

function sanitizeStringValue(value: string): string {
  if (looksSensitive(value)) return "[redacted]";
  return value.slice(0, 128);
}

function looksSensitive(value: string): boolean {
  return (
    value.includes("@") ||
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.length > 256
  );
}
